import { Overlay, OverlayConfig } from '@angular/cdk/overlay';
import { AsyncPipe, CommonModule, NgClass } from '@angular/common';
import { Component, HostBinding, inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ButtonModule } from '@bcef/ia-components/buttons';
import { DatepickerModule } from '@bcef/ia-components/datepicker';
import { HeaderModule } from '@bcef/ia-components/header';
import { InputModule } from '@bcef/ia-components/input';
import { ListboxModule } from '@bcef/ia-components/listbox';
import { PanelModule } from '@bcef/ia-components/panel';
import { TooltipModule } from '@bcef/ia-components/tooltip';
import { IconsModule } from '@bcef/ia-ng-icons';
import { TypologyLiteral } from '@bcef/ia-shared';
import { DataLayered } from '@difa/ia-data-layer';
import { TranslateModule } from '@ngx-translate/core';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { Observable } from 'rxjs';

import { ContractType, RoutesPath } from '@features/termination/enums';
import { TerminationType } from '@features/termination/enums/termination-list.enum';
import { Product } from '@features/termination/models';
import { ErrorMessageComponent } from '@shared/components/error-message/error-message.component';
import { RadioDirective } from '@shared/directives';
import { OTHER } from '@shared/utils';
import { specialCharactersNotAllowed } from '@shared/utils/not-allowed-characters';

import { TerminationInformationForm } from '../../forms';
import { TerminationFacade } from '../../termination.facade';
import { TerminationInformationFacade } from './termination-information.facade';

@DataLayered('screens.terminationInformation.datalayer')
@Component({
  selector: 'ia-resil-contrats-termination-information',
  templateUrl: './termination-information.component.html',
  imports: [
    ErrorMessageComponent,
    TranslateModule,
    ListboxModule,
    NgClass,
    AsyncPipe,
    HeaderModule,
    PanelModule,
    ButtonModule,
    InputModule,
    DatepickerModule,
    TooltipModule,
    CommonModule,
    ReactiveFormsModule,
    IconsModule,
    NgxMaskDirective,
    FormsModule,
    RadioDirective,
  ],
  providers: [provideNgxMask()],
})
export class TerminationInformationComponent implements OnInit {
  @HostBinding('class') public readonly classAttribute =
    'tw-mx-0 tw-flex tw-flex-col tw-pb-10 md:tw-mx-24 hb-group:tw-font-hello-type-next md:tw-mt-10 md:tw-pb-24 hb-group:tw-mx-0';

  private readonly terminationFacade: TerminationFacade = inject(TerminationFacade);

  private readonly terminationInformationFacade: TerminationInformationFacade = inject(
    TerminationInformationFacade,
  );

  public bankingProductValue: ContractType = ContractType.BANKING_PRODUCT;

  public insuranceValue: ContractType = ContractType.INSURANCE;

  public get withdrawValue(): TerminationType {
    return this.terminationInformationFacade.withdrawValue;
  }

  public terminateValue: TerminationType = TerminationType.TERMINATE;

  public other = OTHER;

  public groundsList$: Observable<string[]> = this.terminationInformationFacade.groundsList$;

  public bankingProducts$: Observable<Product[]> =
    this.terminationInformationFacade.bankingProducts$;

  public insurances$: Observable<Product[]> = this.terminationInformationFacade.insurances$;

  public get products$(): Observable<Product[]> {
    return this.contractType.value === this.bankingProductValue
      ? this.bankingProducts$
      : this.insurances$;
  }

  public get typology(): TypologyLiteral {
    return this.terminationFacade.typology;
  }

  public isHb: boolean = this.terminationFacade.isHbGroup;

  public specialCharactersNotAllowed = specialCharactersNotAllowed;

  protected listBoxOverlayConfig: OverlayConfig = {
    scrollStrategy: inject(Overlay).scrollStrategies.close(),
  };

  public ngOnInit(): void {
    this.terminationFacade.updateStepInProgress(2);
    this.terminationInformationFacade.setTerminationInformationFormDefaultValues();
  }

  public get terminationInformationForm(): FormGroup<TerminationInformationForm> {
    return this.terminationInformationFacade.terminationInformationForm;
  }

  public get contractType(): FormControl<string | null> {
    return this.terminationInformationFacade.contractType;
  }

  public get terminationType(): FormControl<string | null> {
    return this.terminationInformationFacade.terminationType;
  }

  public get contract(): FormControl<string | null> {
    return this.terminationInformationFacade.contract;
  }

  public get contractName(): FormControl<string | null> {
    return this.terminationInformationFacade.contractName;
  }

  public get contractNumber(): FormControl<string | null> {
    return this.terminationInformationFacade.contractNumber;
  }

  public get terminationDate(): FormControl<string | null> {
    return this.terminationInformationFacade.terminationDate;
  }

  public get terminationReason(): FormControl<string | null> {
    return this.terminationInformationFacade.terminationReason;
  }

  public get otherReason(): FormControl<string | null> {
    return this.terminationInformationFacade.otherReason;
  }

  public listBoxItemValue(item: Product | Product[] | null): string | Product[] | null {
    if (!Array.isArray(item) && item !== null) {
      return item.value;
    }
    return item;
  }

  public onContactUs(): void {
    this.terminationFacade.openContact();
  }

  public onPrevious(): void {
    this.terminationFacade.navigateTo(RoutesPath.PERSONAL_PAGE);
  }

  public onNext(): void {
    this.terminationInformationForm.markAllAsTouched();
    if (this.terminationInformationForm.invalid) {
      return;
    }
    this.terminationInformationFacade.saveTerminationInformation();
  }
}
